/**
 * Centralized Admin Permission System
 * Single source of truth for role-based access control
 */

export const ADMIN_ROLES = {
  SUPER_ADMIN: 'super_admin',
  ADMIN: 'admin',
}

/**
 * Permission map: role -> accessible pages and features
 */
export const PERMISSIONS = {
  [ADMIN_ROLES.SUPER_ADMIN]: {
    pages: [
      '/admin',
      '/admin/activity-log',
      '/admin/dashboard',
      '/admin/news',
      '/admin/events',
      '/admin/heritage',
      '/admin/partners',
      '/admin/hero',
      '/admin/gallery',
      '/admin/fields-config',
      '/admin/settings',
      '/admin/messages',
      '/admin/admins',
      '/admin/permissions',
      '/admin/profile',
    ],
    features: {
      viewAdminMonitoring: true,
      viewDashboard: true,
      viewNews: true,
      viewEvents: true,
      viewHeritage: true,
      viewPartners: true,
      viewHero: true,
      viewGallery: true,
      viewFields: true,
      viewSettings: true,
      viewMessages: true,
      viewAdmins: true,
      viewPermissions: true,
      viewProfile: true,

      manageAdmins: true,
      manageSettings: true,
      manageMessages: true,
      manageGallery: true,
    },
  },

  [ADMIN_ROLES.ADMIN]: {
    pages: [
      '/admin',
      '/admin/dashboard',
      '/admin/news',
      '/admin/events',
      '/admin/heritage',
      '/admin/gallery',
      '/admin/fields-config',
      '/admin/profile',
    ],
    features: {
      viewDashboard: true,
      viewNews: true,
      viewEvents: true,
      viewHeritage: true,
      viewPartners: false,
      viewHero: false,
      viewGallery: true,
      viewFields: true,
      viewSettings: false,
      viewMessages: false,
      viewAdmins: false,
      viewProfile: true,

      manageAdmins: false,
      manageSettings: false,
      manageMessages: false,
      manageGallery: true,
    },
  },
}

/**
 * Check if role has access to page
 */
export const canAccessPage = (role, page) => {
  const rolePerms = PERMISSIONS[role]
  if (!rolePerms) return false

  if (rolePerms.pages.includes(page)) return true

  return rolePerms.pages.some((allowedPage) => {
    if (allowedPage === '/admin') return false
    return page.startsWith(`${allowedPage}/`)
  })
}

const MODULE_ROUTE_PERMISSIONS = {
  '/admin/news': 'news',
  '/admin/events': 'events',
  '/admin/heritage': 'heritage',
  '/admin/gallery': 'gallery',
  '/admin/fields-config': 'work_fields',
  '/admin/hero': 'hero_slides',
  '/admin/partners': 'partners',
}

/**
 * Check route access using granular module permissions where applicable.
 */
export const canAccessAdminRoute = (admin, page) => {
  if (!admin) return false
  if (admin.role === ADMIN_ROLES.SUPER_ADMIN) return true

  const moduleName = Object.entries(MODULE_ROUTE_PERMISSIONS).find(
    ([route]) => page === route || page.startsWith(`${route}/`)
  )?.[1]

  if (moduleName) return hasPermission(admin, moduleName, 'view')

  return canAccessPage(admin.role, page)
}

/**
 * Check if role has feature access
 */
export const hasFeature = (role, feature) => {
  const rolePerms = PERMISSIONS[role]
  if (!rolePerms) return false
  return rolePerms.features[feature] === true
}

/**
 * Get allowed pages for role
 */
export const getAllowedPages = (role) => {
  const rolePerms = PERMISSIONS[role]
  if (!rolePerms) return []
  return rolePerms.pages
}

/**
 * Get allowed features for role
 */
export const getAllowedFeatures = (role) => {
  const rolePerms = PERMISSIONS[role]
  if (!rolePerms) return {}
  return rolePerms.features
}

/**
 * Sidebar navigation items filtered by role
 */
export const getSidebarItems = (admin, t) => {
  const role = admin?.role || ADMIN_ROLES.ADMIN
  const features = getAllowedFeatures(role)

  const allItems = [
    {
      key: 'dashboard',
      href: '/admin',
      label: t.dashboard,
      isVisible: features['viewDashboard'] === true,
    },
    {
      key: 'activityLog',
      href: '/admin/activity-log',
      label: t.activityLog || (t.lang === 'en' ? 'Activity Log' : 'سجل النشاطات'),
      isVisible: features['viewAdminMonitoring'] === true,
    },
    {
      key: 'news',
      href: '/admin/news',
      label: t.news,
      isVisible: hasPermission(admin, 'news', 'view'),
    },
    {
      key: 'events',
      href: '/admin/events',
      label: t.events,
      isVisible: hasPermission(admin, 'events', 'view'),
    },
    {
      key: 'heritage',
      href: '/admin/heritage',
      label: t.heritage,
      isVisible: hasPermission(admin, 'heritage', 'view'),
    },
    {
      key: 'partners',
      href: '/admin/partners',
      label: t.partners,
      isVisible: hasPermission(admin, 'partners', 'view'),
    },
    {
      key: 'hero',
      href: '/admin/hero',
      label: t.heroSlides,
      isVisible: hasPermission(admin, 'hero_slides', 'view'),
    },
    {
      key: 'gallery',
      href: '/admin/gallery',
      label:
        t.manageGallery ||
        t.gallery ||
        (t.lang === 'en' ? 'Gallery' : 'المعرض'),
      isVisible: hasPermission(admin, 'gallery', 'view'),
    },
    {
      key: 'fields',
      href: '/admin/fields-config',
      label: t.manageFields || (t.lang === 'en' ? 'Fields' : 'مجالات العمل'),
      isVisible: hasPermission(admin, 'work_fields', 'view'),
    },
    {
      key: 'settings',
      href: '/admin/settings',
      label: t.siteSettings,
      isVisible: features['viewSettings'] === true,
    },
    {
      key: 'messages',
      href: '/admin/messages',
      label: t.messages,
      isVisible: features['viewMessages'] === true,
    },
    {
      key: 'admins',
      href: '/admin/admins',
      label: t.admins,
      isVisible: features['viewAdmins'] === true,
    },
    {
      key: 'permissions',
      href: '/admin/permissions',
      label: t.managePermissions || (t.lang === 'en' ? 'Manage Permissions' : 'إدارة الصلاحيات'),
      isVisible: features['viewPermissions'] === true,
    },
    {
      key: 'profile',
      href: '/admin/profile',
      label: t.profile,
      isVisible: features['viewProfile'] === true,
    },
  ]

  return allItems.filter((item) => item.isVisible)
}

/**
 * Dashboard cards filtered by role
 */
export const getDashboardCards = (admin, stats, t) => {
  const role = admin?.role || ADMIN_ROLES.ADMIN
  const features = getAllowedFeatures(role)

  const allCards = [
    {
      label: t.news,
      value: stats.news || 0,
      icon: 'Newspaper',
      color: 'bg-blue-500',
      href: '/admin/news',
      isVisible: hasPermission(admin, 'news', 'view'),
    },
    {
      label: t.events,
      value: stats.events || 0,
      icon: 'Calendar',
      color: 'bg-green-500',
      href: '/admin/events',
      isVisible: hasPermission(admin, 'events', 'view'),
    },
    {
      label: t.heritageLife,
      value: stats.heritage || 0,
      icon: 'Mountain',
      color: 'bg-amber-500',
      href: '/admin/heritage',
      isVisible: hasPermission(admin, 'heritage', 'view'),
    },
    {
      label: t.partners,
      value: stats.partners || 0,
      icon: 'Handshake',
      color: 'bg-sky-500',
      href: '/admin/partners',
      isVisible: hasPermission(admin, 'partners', 'view'),
    },
    {
      label:
        t.gallery ||
        t.manageGallery ||
        (t.lang === 'en' ? 'Gallery' : 'المعرض'),
      value: stats.gallery || 0,
      icon: 'Images',
      color: 'bg-teal-500',
      href: '/admin/gallery',
      isVisible: hasPermission(admin, 'gallery', 'view'),
    },
    {
      label: t.messages,
      value: stats.messages || 0,
      icon: 'MessageSquare',
      color: 'bg-primary',
      href: '/admin/messages',
      badge: stats.unreadMessages || 0,
      isVisible: features['viewMessages'] === true,
    },
    {
      label: t.admins,
      value: stats.admins || 0,
      icon: 'Users',
      color: 'bg-slate-500',
      href: '/admin/admins',
      isVisible: features['viewAdmins'] === true,
    },
  ]

  return allCards.filter((card) => card.isVisible)
}

/**
 * Quick action buttons filtered by role
 */
export const getQuickActions = (admin, t) => {
  const role = admin?.role || ADMIN_ROLES.ADMIN
  const features = getAllowedFeatures(role)

  const allActions = [
    {
      label: t.addNews,
      icon: 'Newspaper',
      href: '/admin/news',
      isVisible: hasPermission(admin, 'news', 'create'),
    },
    {
      label: t.addEvent,
      icon: 'Calendar',
      href: '/admin/events',
      isVisible: hasPermission(admin, 'events', 'create'),
    },
    {
      label: t.addHeritage,
      icon: 'Mountain',
      href: '/admin/heritage',
      isVisible: hasPermission(admin, 'heritage', 'create'),
    },
    {
      label: t.addPartner,
      icon: 'Handshake',
      href: '/admin/partners',
      isVisible: hasPermission(admin, 'partners', 'create'),
    },
    {
      label: t.addSlide,
      icon: 'Images',
      href: '/admin/hero',
      isVisible: hasPermission(admin, 'hero_slides', 'create'),
    },
    {
      label:
        t.manageGallery ||
        t.gallery ||
        (t.lang === 'en' ? 'Manage Gallery' : 'إدارة المعرض'),
      icon: 'Images',
      href: '/admin/gallery',
      isVisible: hasPermission(admin, 'gallery', 'view'),
    },
    {
      label: t.siteSettings,
      icon: 'Settings',
      href: '/admin/settings',
      isVisible: features['viewSettings'] === true,
    },
    {
      label: t.addAdmin,
      icon: 'Users',
      href: '/admin/admins',
      isVisible: features['viewAdmins'] === true,
    },
    {
      label: t.viewSite,
      icon: 'Eye',
      href: '/',
      isVisible: true,
      external: true,
    },
  ]

  return allActions.filter((action) => action.isVisible)
}

/**
 * Check if the admin has granular permissions for a specific module and action.
 * @param {Object} admin - The admin object from AuthContext / localStorage (contains .permissions)
 * @param {string} moduleName - The module to check (e.g., 'news', 'events')
 * @param {string} action - The action to check ('view', 'create', 'update', 'delete')
 * @returns {boolean} True if allowed, false otherwise
 */
export const hasPermission = (admin, moduleName, action) => {
  if (!admin) return false
  if (admin.role === ADMIN_ROLES.SUPER_ADMIN) return true
  
  const perms = admin.permissions
  if (!perms || !perms[moduleName]) return false
  
  return !!perms[moduleName][action]
}

export default {
  ADMIN_ROLES,
  PERMISSIONS,
  canAccessPage,
  hasFeature,
  getAllowedPages,
  getAllowedFeatures,
  getSidebarItems,
  getDashboardCards,
  getQuickActions,
  hasPermission,
  canAccessAdminRoute,
}
